import { Link, useLocation } from "react-router-dom"
import {
    LayoutDashboard,
    Smartphone,
    Package,
    ShoppingCart,
    Megaphone,
    Users,
    Clock,
    DollarSign,
    BookOpen,
    FileBarChart,
    UserCog,
    Store,
    Truck,
    Globe,
    Lock,
    ImagePlus,
    MessageSquareQuote,
    Sparkles
} from "lucide-react"
import logo from "../../assets/logo/gestock.png"
import { useSelector } from "react-redux"
import { getCurrentPlan, hasFeature } from "../../utils/permissions"
import { getStoreProductRules, isCatalogStore } from "../../utils/storeCategory"
import { getStoreExperience } from "../../utils/storeExperience"

type Props = {
    isOpen: boolean
    onClose: () => void
}

export default function Sidebar({ isOpen, onClose }: Props) {
    const { userdata } = useSelector((state: any) => state.UserReducer);
    const currentPlan = getCurrentPlan(userdata)
    const canUseReports = hasFeature(currentPlan, "ADVANCED_REPORTS")
    const canUseMultiStore = hasFeature(currentPlan, "MULTI_STORE")
    const canUseEcommerce = hasFeature(currentPlan, "ECOMMERCE")

    const { store } = userdata;
    const location = useLocation()
    const isGeneralStore = isCatalogStore(store)
    const { isServiceStore } = getStoreProductRules(store)
    const experience = getStoreExperience(store)
    const isActive = (path: string) =>
        location.pathname === path
            ? experience.sidebar.activeClass
            : experience.sidebar.inactiveClass

    const item =
        "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200"

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-black/40 z-40 ${isOpen ? "block lg:hidden" : "hidden"}`}
            />

            <aside
                className={`
          fixed top-4 left-4 z-50
          w-72 h-[calc(100vh-2rem)]
          ${experience.sidebar.bgClass} text-white
          rounded-2xl shadow-2xl
          flex flex-col
          transform transition-transform duration-300
          ${isOpen ? "translate-x-0" : "-translate-x-[calc(100%+1rem)]"}
          lg:translate-x-0
        `}
            >
                {/* Logo */}
                <div className={`px-6 py-6 border-b flex flex-col gap-2 ${experience.sidebar.borderClass}`}>
                    <img
                        src={logo}
                        alt="GESTOCK logo"
                        className="h-17 w-22"
                    />
                    <div className={`inline-flex w-fit rounded-full border px-2.5 py-1 text-[11px] uppercase tracking-[0.18em] ${experience.topbar.badgeClass}`}>
                        {experience.badge}
                    </div>
                </div>


                {/* Menu */}
                <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-6">

                    {/* Dashboard */}
                    <div>
                        <p className={`mb-2 px-2 text-xs uppercase ${experience.sidebar.sectionClass}`}>
                            Vue générale
                        </p>
                        <Link to="/dashboard" onClick={onClose}
                            className={`${item} ${isActive("/")}`}>
                            <LayoutDashboard size={20} />
                            Tableau de bord
                        </Link>
                        <Link to="/ai-assistant" onClick={onClose}
                            className={`${item} ${isActive("/ai-assistant")}`}>
                            <Sparkles size={20} />
                            SPEAKON
                        </Link>
                    </div>

                    {/* Stock */}
                    <div>
                        <p className={`mb-2 px-2 text-xs uppercase ${experience.sidebar.sectionClass}`}>
                            {experience.stockSectionLabel}
                        </p>

                        {isGeneralStore ? (
                            <Link to="/stock" onClick={onClose}
                                className={`${item} ${isActive("/stock")}`}>
                                <Package size={20} />
                                {experience.kind === "car"
                                  ? "Vehicules"
                                  : experience.kind === "property"
                                    ? "Biens"
                                    : experience.kind === "gym"
                                      ? "Articles fitness"
                                      : experience.kind === "service"
                                        ? "Services"
                                        : experience.kind === "fashion"
                                          ? "Articles de mode"
                                          : "Produits"}
                            </Link>
                        ) : (
                            <>
                                <Link to="/models" onClick={onClose} className={`${item} ${isActive("/models")}`}>
                                    <Smartphone size={20} />
                                    Modèles
                                </Link>

                                <Link to="/phones" onClick={onClose}
                                    className={`${item} ${isActive("/phones")}`}>
                                    <Smartphone size={20} />
                                    Téléphones
                                </Link>
                            </>
                        )}

                        {/* 👉 FOURNISSEURS */}
                        <Link to="/suppliers" onClick={onClose}
                            className={`${item} ${isActive("/suppliers")}`}>
                            <Truck size={20} />
                            Fournisseurs
                        </Link>
                        {/* <Link to="/stock" onClick={onClose}
                            className={`${item} ${isActive("/stock")}`}>
                            <Package size={20} />
                            Stock disponible
                        </Link> */}
                    </div>

                    {/* Ventes & Clients */}
                    <div>
                        <p className={`mb-2 px-2 text-xs uppercase ${experience.sidebar.sectionClass}`}>
                            {experience.salesSectionLabel}
                        </p>
                        <Link to="/sales" onClick={onClose}
                            className={`${item} ${isActive("/sales")}`}>
                            <ShoppingCart size={20} />
                            {experience.kind === "property"
                              ? "Opportunites"
                              : experience.kind === "gym"
                                ? "Abonnements & ventes"
                                : experience.kind === "service"
                                  ? "Devis & ventes"
                                  : experience.kind === "fashion"
                                    ? "Ventes & tendances"
                                    : "Ventes"}
                        </Link>

                        <Link to="/orders" onClick={onClose}
                            className={`${item} ${isActive("/orders")}`}>
                            <Package size={20} />
                            Commandes en ligne
                        </Link>

                        <Link to="/clients" onClick={onClose}
                            className={`${item} ${isActive("/clients")}`}>
                            <Users size={20} />
                            {experience.clientsSectionLabel}
                        </Link>
                        <Link to="/campaigns" onClick={onClose}
                            className={`${item} ${isActive("/campaigns")}`}>
                            <Megaphone size={20} />
                            Campagnes SMS
                        </Link>

                        <Link to="/debtors" onClick={onClose}
                            className={`${item} ${isActive("/debtors")}`}>
                            <Clock size={20} />
                            {experience.kind === "gym"
                              ? "Abonnements impayés"
                              : experience.kind === "service"
                                ? "Paiements en attente"
                                : experience.kind === "fashion"
                                  ? "Impayés boutique"
                                  : "Débiteurs"}
                        </Link>
                    </div>

                    {/* Finances */}
                    <div>
                        <p className={`mb-2 px-2 text-xs uppercase ${experience.sidebar.sectionClass}`}>
                            Finances
                        </p>
                        <Link to="/accounting" onClick={onClose} className={`${item} ${isActive("/accounting")}`}><BookOpen size={20} />Comptabilité</Link>
                        <Link to="/expenses" onClick={onClose}
                            className={`${item} ${isActive("/expenses")}`}>
                            <DollarSign size={20} />
                            Dépenses
                        </Link>

                        <Link to="/reports" onClick={onClose}
                            className={`${item} ${canUseReports ? isActive("/reports") : "text-white/55 hover:bg-white/10"}`}>
                            <FileBarChart size={20} />
                            Rapports
                            {!canUseReports && <Lock size={14} className="ml-auto" />}
                        </Link>
                    </div>

                    {/* Admin */}
                    <div>
                        <p className={`mb-2 px-2 text-xs uppercase ${experience.sidebar.sectionClass}`}>
                            Administration
                        </p>
                        <Link to="/users" onClick={onClose}
                            className={`${item} ${isActive("/users")}`}>
                            <UserCog size={20} />
                            Utilisateurs
                        </Link>
                        <Link to="/stores" onClick={onClose}
                            className={`${item} ${canUseMultiStore ? isActive("/stores") : "text-white/55 hover:bg-white/10"}`}>
                            <Store size={20} />
                            Boutiques
                            {!canUseMultiStore && <Lock size={14} className="ml-auto" />}
                        </Link>
                        {canUseEcommerce ? (
                            <Link to="/ecommerce-request" onClick={onClose}
                                className={`${item} ${isActive("/ecommerce-request")}`}>
                                <Globe size={20} />
                                E-commerce
                            </Link>
                        ) : (
                            <div className={`${item} text-white/55`}>
                                <Globe size={20} />
                                E-commerce (Business)
                                <Lock size={14} className="ml-auto" />
                            </div>
                        )}
                        <Link to="/activity" onClick={onClose}
                            className={`${item} ${isActive("/activity")}`}>
                            <Clock size={20} />
                            Historique actions
                        </Link>
                    </div>

                    {isServiceStore ? (
                        <div>
                            <p className={`mb-2 px-2 text-xs uppercase ${experience.sidebar.sectionClass}`}>
                                Vitrine services
                            </p>
                            <Link to="/service-showcase" onClick={onClose}
                                className={`${item} ${isActive("/service-showcase")}`}>
                                <ImagePlus size={20} />
                                Nos realisations
                            </Link>
                            <Link to="/service-reviews" onClick={onClose}
                                className={`${item} ${isActive("/service-reviews")}`}>
                                <MessageSquareQuote size={20} />
                                Reviews
                            </Link>
                        </div>
                    ) : null}

                </nav>

                {/* Footer */}
                <div className={`px-6 py-4 border-t text-xs ${experience.sidebar.borderClass} ${experience.sidebar.footerClass}`}>
                    © {new Date().getFullYear()} GESTOCK | BY LSD Sarl.
                </div>
            </aside >
        </>
    )
}
